/**
 * File: servicesData.ts
 * Path: packages/config/src/pricing/servicesData.ts
 * Description: Standalone services using the standardized Investment structure.
 */
import { Service } from "./pricingTypes";
import { STANDARD_PAYMENT_OPTIONS } from "./paymentConstants";

/** Lesson #2726: a guarantee is an if-then with a consequence, or it is nothing. */
export const FIXED_PRICE_GUARANTEE =
  "Fixed price. If the agreed work takes us longer than planned, the extra time is on us.";

/** Days after the blueprint is delivered in which its fee still comes off the build. */
export const BLUEPRINT_CREDIT_DAYS = 30;

/** How the blueprint credit reads wherever the fee is shown. */
export const BLUEPRINT_CREDIT_NOTE = `The full fee comes off your build price if you go ahead within ${BLUEPRINT_CREDIT_DAYS} days.`;

/** The promise on services that take admin off the owner's plate. */
export const HOURS_SAVED_GUARANTEE =
  "We agree up front on the admin hours it should save you each week. If it doesn't, we keep working on it free until it does.";

const PROFESSIONAL_EMAIL: Service = {
  id: "util-email-setup",
  name: "Professional Business Email",
  type: "service",
  category: "optimisation",
  tagline:
    "Emails from you@yourbusiness that land in the inbox and make you look established.",
  bestFor: "Businesses still sending quotes from a Gmail or Yahoo address.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 1000, structure: "once-off", term: "One-time setup" },
  timeline: "3 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Custom Email Address",
      description:
        "A professional @yourcompany.com address, set up on your phone and computer.",
      deliverable: "1x Working Email Address",
    },
    {
      title: "Spam & Deliverability Protection",
      description:
        "Correct technical settings so your emails reach the inbox instead of getting flagged as spam.",
      deliverable: "Verified Settings",
    },
    {
      title: "Branded Email Signature",
      description:
        "Every new email you draft automatically includes a professional footer with your logo, contact details, and brand colours.",
      deliverable: "Auto-Applied Signature",
    },
  ],
  tags: ["core", "spark"],
};

const LOCAL_SEARCH_MAPS: Service = {
  id: "util-google-maps",
  name: "Get Found on Google Maps",
  type: "service",
  category: "marketing",
  tagline:
    'Show up when locals search "near me", with your hours, photos and reviews in one place.',
  bestFor: "Any business that customers visit, call or book locally.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 2500, structure: "once-off", term: "One-time setup" },
  timeline: "7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Verified Google Business Profile",
      description:
        "Your business claimed, verified, and fully filled in with hours, photos, and contact info.",
      deliverable: "Verified Profile",
    },
    {
      title: "Local Search Visibility",
      description:
        "Optimised so you appear when nearby customers search for your service.",
      deliverable: "Optimised Listing",
    },
  ],
  tags: ["visibility", "spark"],
};

const ONE_PAGE_LANDING: Service = {
  id: "web-one-page-landing",
  name: "One-Page Enquiry Site",
  type: "service",
  category: "web",
  tagline:
    "One page with one job: getting people to call, message or book you. Live in 7 days.",
  bestFor: "New businesses, or one offer or launch that needs its own page.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 1600, structure: "once-off", term: "Standard build" },
  timeline: "7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Live Single-Page Website",
      description:
        "A clean, mobile-friendly page covering who you are, what you offer, and how to contact you.",
      deliverable: "Live Website",
    },
    {
      title: "Clear Call-to-Action",
      description:
        "Designed around one goal: getting visitors to message, call, or book you.",
      deliverable: "Contact/Booking Button",
    },
  ],
  tags: ["core", "spark"],
};

const STARTER_WEBSITE: Service = {
  id: "web-starter-5-page",
  name: "5-Page Business Website",
  type: "service",
  category: "web",
  tagline:
    "Everything a customer checks before they call you, on a site that works properly on their phone.",
  bestFor:
    "Established businesses with no website, or one they're embarrassed to share.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 6500, structure: "once-off", term: "Standard build" },
  timeline: "14 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "5-Page Website",
      description:
        "Home, About, Services, Gallery, and Contact pages, fully built and live.",
      deliverable: "Live Website",
    },
    {
      title: "Mobile-Friendly Design",
      description:
        "Looks and works properly on phones, where most of your customers will find you.",
      deliverable: "Responsive Layout",
    },
  ],
  tags: ["core", "spark"],
};

const UNLIMITED_WEBSITE: Service = {
  id: "web-unlimited",
  name: "Website With Booking Built In",
  type: "service",
  category: "web",
  tagline:
    "Customers book, order and pay online while you work. No more back-and-forth on WhatsApp.",
  bestFor:
    "Booking and service businesses: transport, salons, trades, studios and clinics.",
  guarantee: HOURS_SAVED_GUARANTEE,
  investment: { amount: 15500, structure: "once-off", term: "Full build" },
  timeline: "21-30 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Built-In Booking & Ordering",
      description:
        "Customers can book appointments, place orders, or pay online directly from your site.",
      deliverable: "Live Booking/Till System",
    },
    {
      title: "Unlimited Pages",
      description:
        "Add as many pages, products, or services as your business needs. No limits.",
      deliverable: "Live Unlimited Site",
    },
  ],
  tags: ["growth", "ignite"],
};

const TECH_HUB: Service = {
  id: "app-tech-hub",
  name: "Business App",
  type: "service",
  category: "app",
  tagline:
    "Your own app for you and your customers, live on the App Store and Google Play.",
  bestFor:
    "One business and its customers: bookings, orders, loyalty and updates in one app.",
  guarantee: HOURS_SAVED_GUARANTEE,
  investment: {
    amount: 28500,
    structure: "once-off",
    term: "Two-interface build",
  },
  timeline: "30-45 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Customer App",
      description:
        "Your customers book, order, pay and get updates in an app with your name on it, on both app stores.",
      deliverable: "Live Customer App",
    },
    {
      title: "Owner Dashboard",
      description:
        "One screen to see and manage bookings, orders, customers and payments, on your phone or computer.",
      deliverable: "Admin Dashboard",
    },
    {
      title: "Automated Daily Tools",
      description:
        "Reminders, confirmations and follow-ups that go out on their own, so you stop doing them by hand.",
      deliverable: "Connected Automations",
    },
  ],
  tags: ["scale", "blaze", "app"],
};

/**
 * Apps are priced by how many kinds of user they serve: every extra interface
 * is roughly one more build stage plus testing. Two interfaces is the
 * Business App; this adds staff. A marketplace (vendors, super admin) is
 * quoted after the blueprint, from APP_MARKETPLACE_FROM.
 */
const TEAM_APP: Service = {
  id: "app-team",
  name: "Team App",
  type: "service",
  category: "app",
  tagline:
    "One app for you, your customers and your staff, so jobs, schedules and updates run without phone calls.",
  bestFor:
    "Businesses with staff out on jobs: cleaning, courier, maintenance, transport and field services.",
  guarantee: HOURS_SAVED_GUARANTEE,
  investment: {
    amount: 55000,
    structure: "once-off",
    term: "Three-interface build",
  },
  timeline: "45-60 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Everything in the Business App",
      description:
        "The customer app, your owner dashboard and the automated daily tools.",
      deliverable: "Customer App + Dashboard",
    },
    {
      title: "Staff App",
      description:
        "Your team sees their jobs and schedule, checks in and out, and updates the customer from the job.",
      deliverable: "Live Staff App",
    },
    {
      title: "Roles & Permissions",
      description:
        "Each person sees only what they need: customers, staff and you each get their own view.",
      deliverable: "Role-Based Access",
    },
  ],
  tags: ["scale", "app"],
};

/** Marketplace apps (4-5 interfaces) start here; the final price follows the blueprint. */
export const APP_MARKETPLACE_FROM = 95000;

const FOUNDATIONAL_BRANDING: Service = {
  id: "brand-foundational",
  name: "Professional Brand Look",
  type: "service",
  category: "branding",
  tagline:
    "A logo and brand kit that make you look established from the first impression.",
  bestFor:
    "New businesses, or owners whose look no longer matches the quality of their work.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 3500, structure: "once-off", term: "One-time setup" },
  timeline: "7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Custom Logo",
      description:
        "A logo designed specifically for your business, delivered in all the formats you'll need.",
      deliverable: "Final Brand Kit",
    },
    {
      title: "Print-Ready Files",
      description:
        "Ready-to-use files for signage, business cards, and packaging.",
      deliverable: "Print-Ready Assets",
    },
  ],
  tags: ["core", "spark"],
};

const ACTIVE_MARKETING: Service = {
  id: "marketing-active",
  name: "Keep Customers Coming Back",
  type: "service",
  category: "marketing",
  tagline:
    "Thank-you messages, review requests and repeat-visit offers that go out automatically.",
  bestFor:
    "Businesses whose past customers would come back if someone reminded them.",
  guarantee: HOURS_SAVED_GUARANTEE,
  investment: { amount: 4500, structure: "once-off", term: "Marketing setup" },
  timeline: "14 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Automated Loyalty System",
      description:
        "Thank-you messages and repeat-visit offers sent automatically, no manual work needed.",
      deliverable: "Live Email System",
    },
    {
      title: "Customer Win-Back Emails",
      description:
        "Automatic reminders to customers who haven't visited or bought in a while.",
      deliverable: "Automated Sequence",
    },
  ],
  tags: ["automation", "ignite"],
};

const ADVANCED_GROWTH: Service = {
  id: "marketing-advanced-growth",
  name: "Find More Customers",
  type: "service",
  category: "marketing",
  tagline:
    "Better Google rankings and stronger service pages, so more locals find you before your competitors.",
  bestFor: "Businesses with a website that brings in few enquiries.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 8500, structure: "once-off", term: "Growth setup" },
  timeline: "21 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Improved Google Rankings",
      description:
        "SEO updates focused on getting you found by people searching in your area.",
      deliverable: "Optimised Website",
    },
    {
      title: "Higher-Converting Pages",
      description:
        "Sales pages rewritten to turn more visitors into paying customers.",
      deliverable: "Updated Sales Pages",
    },
  ],
  tags: ["scale", "blaze"],
};

const PERFORMANCE_SSR: Service = {
  id: "ops-ssr-upgrade",
  name: "Instant-Load Speed",
  type: "service",
  category: "optimisation",
  tagline:
    "A faster website that loads before visitors give up, and ranks better on Google.",
  bestFor: "Sites that feel slow on a phone, especially over mobile data.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 5500, structure: "once-off", term: "Speed upgrade" },
  timeline: "7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Faster Load Times",
      description:
        "Your site upgraded to load near-instantly, especially on mobile data.",
      deliverable: "Upgraded Engine",
    },
    {
      title: "Better Google Rankings",
      description:
        "Faster sites are favoured by Google, helping you rank higher automatically.",
      deliverable: "SEO Benefit",
    },
  ],
  tags: ["performance", "ignite"],
};

const BUSINESS_TRACKING: Service = {
  id: "ops-tracking-reports",
  name: "Clear Business Numbers",
  type: "service",
  category: "optimisation",
  tagline:
    "See what your website brings in each week, in plain English, without logging into anything.",
  bestFor:
    "Owners who can't say how many enquiries their site brought in last month.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 3500, structure: "once-off", term: "Data setup" },
  timeline: "10 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Private Dashboard",
      description:
        "A simple, private webpage showing your visitor and sales numbers.",
      deliverable: "Tracking Dashboard",
    },
    {
      title: "Weekly Performance Emails",
      description:
        "A short email each week summarising how your business is tracking online.",
      deliverable: "Weekly Reports",
    },
  ],
  tags: ["data", "blaze"],
};

const STRATEGY_AUDIT: Service = {
  id: "ops-strategy-audit",
  name: "90-Day Game Plan",
  type: "service",
  category: "optimisation",
  tagline:
    "A clear, written plan for your next 90 days, and a call to walk you through it.",
  bestFor: "Owners who know they need to grow online but not what to do first.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: {
    amount: 2500,
    structure: "once-off",
    term: "Consulting session",
  },
  timeline: "3 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "1-on-1 Strategy Call",
      description:
        "A 45-minute video call to map out where to focus for the biggest growth.",
      deliverable: "45-Min Call",
    },
    {
      title: "Written 90-Day Checklist",
      description:
        "A step-by-step action plan you can follow with or without us.",
      deliverable: "Strategy Document",
    },
  ],
  tags: ["strategy", "ignite"],
};

const MASTER_PLAN: Service = {
  id: "service-master-plan",
  name: "App Blueprint & Prototype",
  type: "service",
  category: "app",
  tagline:
    "See your app working before you invest in building it: a clear blueprint and a clickable prototype of its main feature.",
  bestFor:
    "Anyone with an app or marketplace idea who wants to see it before committing to the full build.",
  guarantee: `Fixed price. ${BLUEPRINT_CREDIT_NOTE}`,
  investment: {
    amount: 5000,
    structure: "once-off",
    term: "Blueprint & prototype",
  },
  timeline: "7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Technical Blueprint",
      description:
        "A clear document mapping how your app and its integrations will work, before any building starts.",
      deliverable: "Blueprint PDF",
    },
    {
      title: "Clickable Prototype",
      description:
        "Your app's main selling point, clickable on a phone, so you can show it to customers or partners. It shows the core feature only, not every part working together.",
      deliverable: "Clickable Prototype",
    },
    {
      title: "Fixed Build Quote",
      description: `Which app tier fits, what gets built in what order, and a fixed price for the full build. Go ahead within ${BLUEPRINT_CREDIT_DAYS} days and the blueprint fee comes off that price.`,
      deliverable: "Scope, Timeline & Quote",
    },
  ],
  tags: ["forge", "strategy"],
};

const LOGO_DESIGN: Service = {
  id: "brand-logo-design",
  name: "Logo Design",
  type: "service",
  category: "branding",
  tagline:
    "A custom logo, plus the story behind why it works for your customers.",
  bestFor: "Businesses that need a logo and nothing more yet.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 1200, structure: "once-off", term: "One-time setup" },
  timeline: "7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Custom Logo Design",
      description:
        "A logo designed specifically for your business, not a generic template.",
      deliverable: "Final Logo Design",
    },
    {
      title: "Concept Presentation",
      description:
        "A short deck walking you through the reasoning: why the shapes, colours, and fonts were chosen.",
      deliverable: "Concept Deck",
    },
    {
      title: "All Logo Files",
      description:
        "Every format you'll need for web, print, social, and transparent backgrounds.",
      deliverable: "Complete File Pack",
    },
  ],
  tags: ["core", "spark"],
};

const VIDEO_PROMO_EDIT: Service = {
  id: "media-video-promo-edit",
  name: "Video Promo Editing",
  type: "service",
  category: "marketing",
  tagline:
    "Send us your phone clips; get back a short before-and-after video that shows people the result.",
  bestFor:
    "Hands-on businesses with visible results: cleaning, pools, trades, detailing, food.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: { amount: 1500, structure: "once-off", term: "Editing only" },
  timeline: "5-7 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "30-60 Second Promo Video",
      description:
        "Edited from footage and photos you provide, built around your concept.",
      deliverable: "Final Promo Video",
    },
    {
      title: "Music, Captions & Branding",
      description:
        "Sound, on-screen text, and your logo/brand colours added throughout.",
      deliverable: "Fully Edited Video",
    },
    {
      title: "One Round of Revisions",
      description: "One set of changes included, asked for within 7 days of your first cut.",
      deliverable: "Revised Final Cut",
    },
  ],
  tags: ["marketing", "ignite"],
};

const VIDEO_PROMO_FULL_PRODUCTION: Service = {
  id: "media-video-promo-production",
  name: "Video Promo Full Production",
  type: "service",
  category: "marketing",
  tagline:
    "Bring us the idea. We script it, shoot or source it, and edit it into a promo that sells.",
  bestFor: "Businesses launching something new that needs to look its best.",
  guarantee: FIXED_PRICE_GUARANTEE,
  investment: {
    amount: 5000,
    structure: "once-off",
    term: "Base fee + at-cost content expenses",
  },
  timeline: "14-21 days",
  paymentOptions: STANDARD_PAYMENT_OPTIONS,
  features: [
    {
      title: "Concept & Scriptwriting",
      description:
        "We develop the idea, storyline, and shot plan from scratch.",
      deliverable: "Script & Shot Plan",
    },
    {
      title: "Content Sourced or Created for You",
      description:
        "Footage, stock, models, or locations arranged on your behalf. Any content costs are quoted and approved upfront before we spend anything.",
      deliverable: "Quoted Before Spend",
    },
    {
      title: "Full Edit & Final Delivery",
      description: "A polished 30-60 second promo video, ready to publish.",
      deliverable: "Final Promo Video",
    },
  ],
  tags: ["marketing", "blaze"],
};

export const SERVICES = {
  professionalEmail: PROFESSIONAL_EMAIL,
  localSearch: LOCAL_SEARCH_MAPS,
  onePageLanding: ONE_PAGE_LANDING,
  starterWebsite: STARTER_WEBSITE,
  unlimitedWebsite: UNLIMITED_WEBSITE,
  techHub: TECH_HUB,
  teamApp: TEAM_APP,
  foundationalBranding: FOUNDATIONAL_BRANDING,
  activeMarketing: ACTIVE_MARKETING,
  advancedGrowth: ADVANCED_GROWTH,
  performanceSSR: PERFORMANCE_SSR,
  businessTracking: BUSINESS_TRACKING,
  strategyAudit: STRATEGY_AUDIT,
  masterPlan: MASTER_PLAN,
  logoDesign: LOGO_DESIGN,
  videoPromoEdit: VIDEO_PROMO_EDIT,
  videoPromoFullProduction: VIDEO_PROMO_FULL_PRODUCTION,
} as const satisfies Record<string, Service>;
/**
 * Services that stay in code, quotes and packages but are not listed on the
 * front of the site (homepage, services list). Deep links and carts still work.
 */
export const SECONDARY_SERVICE_IDS: readonly string[] = [
  "marketing-advanced-growth",
  "marketing-active",
  "ops-tracking-reports",
  "media-video-promo-production",
  "media-video-promo-edit",
  "ops-strategy-audit",
];
